import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import {
  CareRecipient,
  InvitationInfo,
  InvitationRespondRequest,
  InvitationRespondResponse,
  RevokeResponse
} from './care-links-api.models';

/**
 * Pure API service for care link HTTP operations
 * No state management - just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class CareLinksApiService {
  private readonly logger = inject(LoggerService).scope('CareLinksApiService');

  private readonly protectedUrl = `${environment.API_BASE_URL}/protected/care/links`;
  private readonly protectedInvitationUrl = `${environment.API_BASE_URL}/protected/care/invitation`;
  private readonly publicUrl = `${environment.API_BASE_URL}/public/care/invitation`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /protected/care/links/my-care-recipients
   * Lists users whose analytics the current user can view.
   * Self is always the first entry.
   */
  public getMyCareRecipients(): Observable<CareRecipient[]> {
    return this.http.get<CareRecipient[]>(`${this.protectedUrl}/my-care-recipients`).pipe(
      catchError(error => {
        this.logger.error('Error fetching care recipients:', error);
        return throwError(() => new Error('Failed to fetch care recipients'));
      })
    );
  }

  /**
   * DELETE /protected/care/links/{linkId}/revoke
   * Either side can revoke an active or pending link.
   */
  public revokeLink(linkId: string): Observable<RevokeResponse> {
    return this.http.delete<RevokeResponse>(`${this.protectedUrl}/${linkId}/revoke`).pipe(
      catchError(error => {
        this.logger.error('Error revoking care link:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * GET /public/care/invitation/info?token=xxx
   * Fetch invitation details for the accept/decline page. No auth required.
   */
  public getInvitationInfo(token: string): Observable<InvitationInfo> {
    return this.http.get<InvitationInfo>(this.publicUrl + '/info', {
      params: { token }
    }).pipe(
      catchError(error => {
        this.logger.error('Error fetching invitation info:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * POST /protected/care/invitation/respond
   * Accept or decline an invitation. Auth required - interceptor adds Bearer token.
   */
  public respondToInvitation(data: InvitationRespondRequest): Observable<InvitationRespondResponse> {
    return this.http.post<InvitationRespondResponse>(this.protectedInvitationUrl + '/respond', data).pipe(
      catchError(error => {
        this.logger.error('Error responding to invitation:', error);
        return throwError(() => error);
      })
    );
  }
}
